import React, { useEffect, useState } from 'react';
import { CalendarDays, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { fittingAPI } from '../../services/api';
import { useT, i18n } from '../../services/i18n';

const dayLabel = (d) => new Date(`${d.date}T12:00`).toLocaleDateString(i18n.locale(), { weekday: 'short', day: 'numeric', month: 'short' });

// Checkout: pick an opening slot at the selected centre (optional — the centre can still propose a date)
const FittingSlotPicker = ({ centreId, centreName, value, onChange }) => {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!centreId) { setData(null); return; }
    setLoading(true); setPage(0);
    fittingAPI.slots(centreId).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [centreId]);
  if (!centreId) return null;
  if (loading) return <p className="mt-3 text-xs text-gray-500 inline-flex items-center gap-1" data-testid="slot-loading"><Loader2 size={12} className="animate-spin" /> {t('slot.loading')}</p>;
  if (!data || !data.has_schedule || !data.days.length) return <p className="mt-3 text-xs text-gray-500" data-testid="slot-unavailable">{t('slot.unavailable')}</p>;
  const PER = 4;
  const pages = Math.ceil(data.days.length / PER);
  const visible = data.days.slice(page * PER, page * PER + PER);
  const chosen = data.days.flatMap((d) => d.times).find((tm) => tm.iso === value);
  return (
    <div className="mt-4 rounded-lg border border-orange-200 bg-orange-50/40 p-3" data-testid="slot-picker">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <p className="text-sm font-semibold text-gray-900 inline-flex items-center gap-1.5"><CalendarDays size={15} className="text-orange-500" /> {t('slot.choose', { name: centreName })}</p>
        <label className="text-xs text-gray-700 inline-flex items-center gap-1.5 cursor-pointer" data-testid="slot-none">
          <input type="radio" name="fitting-slot" checked={!value} onChange={() => onChange(null)} className="text-orange-500" /> {t('slot.centreProposes')}
        </label>
      </div>
      <p className="text-[11px] text-gray-500 mb-2">{t('slot.info', { min: data.slot_minutes, d: data.min_lead_days, count: data.min_lead_days })}</p>
      <div className="flex items-start gap-1">
        <button type="button" onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="mt-1 p-1 rounded text-gray-500 disabled:opacity-30 hover:bg-white" data-testid="slot-prev"><ChevronLeft size={16} /></button>
        <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {visible.map((d) => (
            <div key={d.date} data-testid={`slot-day-${d.date}`}>
              <p className="text-xs font-semibold text-gray-800 mb-1 capitalize">{dayLabel(d)}</p>
              <div className="flex flex-col gap-1 max-h-44 overflow-y-auto pr-0.5">
                {d.times.map((tm) => (
                  <button key={tm.iso} type="button" disabled={!tm.available} onClick={() => onChange(tm.iso)} data-testid={`slot-${tm.iso}`}
                    className={`text-xs rounded border px-2 py-1 transition-colors ${value === tm.iso ? 'bg-orange-500 border-orange-500 text-white font-semibold' : tm.available ? 'bg-white border-gray-300 text-gray-800 hover:border-orange-400' : 'bg-gray-100 border-gray-200 text-gray-400 line-through cursor-not-allowed'}`}>
                    {tm.time}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => setPage(Math.min(pages - 1, page + 1))} disabled={page >= pages - 1} className="mt-1 p-1 rounded text-gray-500 disabled:opacity-30 hover:bg-white" data-testid="slot-next"><ChevronRight size={16} /></button>
      </div>
      {chosen && <p className="mt-2 text-xs text-green-700 font-medium" data-testid="slot-chosen">{t('slot.chosen', { day: dayLabel(data.days.find((d) => d.times.includes(chosen))), time: chosen.time })}</p>}
    </div>
  );
};

export default FittingSlotPicker;
